/*
 * Daily Tracking Summary Report - Controller
 * Angular module: trackerbase-app.views.trackerbase-reports.daily-tracking-summary-report
 * State: tbReports.dailyTrackingSummary
 * Exposed Controller: DailySummaryReportCtrl
 * Controller in template as: dailySummaryReport
 *
 */

import angular from "angular";
import moment from "moment-timezone";

import Assert from "tbAssert";
import MessageQueue from "tbClient/models/MessageQueue";
import { GlobalViewDefinition } from "tbClient/models/GlobalView";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrap from "angular-ui-bootstrap";
import uiRouter from "@uirouter/angularjs";
import kendoDirectives from "tbClient/shims/kendoDirectives";

import TrackerBaseReportsDataServiceModule from "tbClient/services/TrackerbaseReportsDataService";
import FormatTimeZoneFilterModule from "tbClient/filters/FormatTimeZoneNameFilter";
import CurrentTimeZoneServiceModule from "tbClient/services/CurrentTimeZoneService";
import AllOfficeTypesServiceModule from "tbClient/services/AllOfficeTypesService";
import StatusLabelComponentModule from "tbClient/directives/StatusLabel";
import UserMessagesServiceModule from "tbClient/services/UserMessagesService";
import CalendarGlobalViewServiceModule from "tbClient/services/CalendarGlobalViewService";
import TrackingSummaryReportData from "tbClient/views/trackerbase-reports/report-models/TrackingSummaryReportData";

const pageTitleBase = "TrackerBase: Daily Tracking Summary";

const defaultMessageTimeout = 2000;
const defaultMessageType = "info";
const errorMessageType = "danger";
const successMessageType = "success";

const displayModes = Object.freeze({
    Initial: "Initial",
    EditMode: "EditMode",
    PrintMode: "PrintMode"
});

//FIXME: Pull this from stored data rather than hard coding it here;
const accessDeniedStatusName = "Tracked - Access Denied";

const mScopeSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mFilterSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();

const mTrackerBaseReportsDataSvc = new WeakMap();
const mCurrentTimeZoneSvc = new WeakMap();
const mAllOfficeTypesSvc = new WeakMap();
const mUserMessagesSvc = new WeakMap();
const mGlobalViewSvc = new WeakMap();

const reportBoundsTransferFormat = "M/D/YYYY h:mm A";
const reportBoundsPrettyFormat = "MMMM D, YYYY h:mm A";
const reportBoundsPrettyNoTimeFormat = "MMMM D, YYYY";

const mDisplayMode = new WeakMap();
const mUiMessages = new WeakMap();
const mRemoveGlobalViewWatcherFn = new WeakMap();

const mFeatureToggles = new WeakMap();

const dateTimePickerOptions = Object.freeze({
    format: "M/d/yyyy h:mm tt", /* This kendo date format string and the moment.js format string
                                 * in reportBoundsTransferFormat *must* match semantically -
                                 * they will be used to transfer date/times between kendo objects
                                 * and this controller in string format (to avoid any *helpful*
                                 * time zone modification by the built-in Date object).
                                 **/
    interval: 15
});

const dateTimePickerModelOptions = Object.freeze({
    updateOn: "blur default",
    deBounce: Object.freeze({
        blur: 0,
        default: 500
    })
});

const minutesRoundInterval = 15;
function roundMinutesDown(mins, roundTo) {
    return (mins - (mins % roundTo));
}

class OfficeTypeEventsBlockViewModel {
    constructor({oType, reportedEvents = []}) {
        this._oType = oType;
        this._reportedEvents = reportedEvents;
        Assert.StrictAssert(Array.isArray(reportedEvents), "Reported Events data not an array");
    }

    get oType() { return this._oType; }
    get oTypeId() { return this._oType.officeTypeId; }
    get reportedEvents() { return this._reportedEvents; }

    get hasEvents() { return this._reportedEvents.length > 0; }
}

class TrackingSummaryReportController {
    constructor($scope, $document, $log, $filter, $timeout, $state, TrackerBaseReportsData, CurrentTimeZone, AllOfficeTypes, CalendarGlobalViewService, UserMessages)
    {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($filter != null, "Assertion Failure: Missing Filter Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");

        Assert.StrictAssert(TrackerBaseReportsData != null, "Assertion Failure: Missing TrackerBase Reports Data Service Dependency");
        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone Service Dependency");
        Assert.StrictAssert(AllOfficeTypes != null, "Assertion Failure: Missing All Office Types Service Dependency");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Assertion Failure: Missing Global View Service Dependency");
        Assert.StrictAssert(UserMessages != null, "Assertion Failure: Missing User Messages Service Dependency");

        // Silence warning about unused var
        angular.noop();

        mScopeSvc.set(this, $scope);
        mDocumentSvc.set(this, $document);
        mLogSvc.set(this, $log);
        mFilterSvc.set(this, $filter);
        mTimeoutSvc.set(this, $timeout);

        mTrackerBaseReportsDataSvc.set(this, TrackerBaseReportsData);
        mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        mAllOfficeTypesSvc.set(this, AllOfficeTypes);
        mGlobalViewSvc.set(this, CalendarGlobalViewService);
        mUserMessagesSvc.set(this, UserMessages);
        mRemoveGlobalViewWatcherFn.set(this, null);

        mDocumentSvc.get(this).attr("title", pageTitleBase);

        mUiMessages.set(this, new MessageQueue(defaultMessageType, defaultMessageTimeout));

        mFeatureToggles.set(this, Object.assign({}, $state.current.data.featureToggles));

        this._reportData = new TrackingSummaryReportData({
            reportBoundsParams: {
                initialTimeZone: CurrentTimeZone.get(),
                transferFormat: reportBoundsTransferFormat,
                prettyFormat: reportBoundsPrettyFormat,
                prettyNoTimeFormat: reportBoundsPrettyNoTimeFormat,
                boundsInitializerFn: (initTimeZone) => {
                    let currentMoment = moment().tz(initTimeZone);
                    let currentMinute = currentMoment.minute();
                    let adjMinute = roundMinutesDown(currentMinute, minutesRoundInterval);
                    currentMoment.minute(adjMinute);

                    // For simplicity and transparency, discard both the
                    // second and millisecond precision.
                    currentMoment.second(0);
                    currentMoment.millisecond(0);

                    let endMoment = currentMoment.clone();
                    let startMoment = currentMoment.clone().subtract(1, "days");

                    return {
                        start: startMoment,
                        end: endMoment
                    };
                }
            },
            accessDeniedStatus: accessDeniedStatusName
        });
        this._showPrintOptions = false;

        mDisplayMode.set(this, displayModes.Initial);
    }
    
    get messages() {
        return mUiMessages.get(this).messages;
    }

    get reportBounds() {
        return this._reportData.reportBounds;
    }

    get reportedEventsData() {
        return this._reportData.reportedEvents;
    }

    get dateTimePickerOptions() {
        return dateTimePickerOptions;
    }

    get dateTimePickerModelOptions() {
        return dateTimePickerModelOptions;
    }

    get eventStatistics() {
        return this._reportData.reportEventStatistics;
    }

    get displayMode() {
        return mDisplayMode.get(this);
    }

    get sendReportEnabled() {
        const toggleSetting = mFeatureToggles.get(this).sendReportFeature;
        return toggleSetting !== false;
    }
    
    removeMessage(msgIdx) {
        mUiMessages.get(this).removeMessageAtIdx(msgIdx);
    }

    showInCurrentTimeZone(evtMoment) {
        if (moment.isMoment(evtMoment)) {
            return evtMoment.clone().tz(this.reportBounds.reportTimeZone).format("h:mm A");
        }
        else {
            return undefined;
        }
    }

    generateReport() {
        // Remove the global view watcher if it exists.
        const removeGlobalViewWatchFn = mRemoveGlobalViewWatcherFn.get(this);
        if ( removeGlobalViewWatchFn != null) {
            removeGlobalViewWatchFn.apply(this, undefined);
        }

        mTrackerBaseReportsDataSvc.get(this).getEventsReportedInRange(
            this.reportBounds.reportStart,
            this.reportBounds.reportEnd
        )
        .then(
            (eventsData) => {
                this._reportData.clearEvents();

                let processedEventsData = eventsData.map((officeTypeEventEntry) => {
                    let officeType = mAllOfficeTypesSvc.get(this).getFromName(officeTypeEventEntry.officeTypeName);

                    officeTypeEventEntry.reportedEvents.forEach((evt) => {
                        this.eventStatistics.addEvent(evt);
                    });

                    return new OfficeTypeEventsBlockViewModel({
                        oType: officeType,
                        reportedEvents: officeTypeEventEntry.reportedEvents
                    });
                });

                Array.prototype.push.apply(
                    this.reportedEventsData,
                    mFilterSvc.get(this)("orderBy")(
                        processedEventsData,
                        (ote) => { return ote.sortAs; }
                    )
                );

            },
            (errorResponse) => {
                mLogSvc.get(this).error("Error getting daily summary report data", errorResponse);
                mUiMessages.get(this).publishMessage(
                    "Error fetching data for report. Please check the console and file a bug report.", 
                    MessageQueue.timeoutOverrideValue,
                    errorMessageType
                );
            }
        )
        .then(
            () => {
                mUiMessages.get(this).publishMessage("Data Loaded");
                let currentNgScope = mScopeSvc.get(this);

                if (currentNgScope.dailySummaryReportForm != null)
                {
                    currentNgScope.dailySummaryReportForm.$setPristine();
                }

                mDisplayMode.set(this, displayModes.EditMode);

                const unsetGlobalViewWatchFn = currentNgScope.$watch(
                    () => mGlobalViewSvc.get(this).getCurrentView(),
                    (newVal, oldVal) => {
                        if (GlobalViewDefinition.WatchComparer(newVal, oldVal)) {
                            this.generateReport(); //Recursive if we don't act very carefully!
                        }
                    }
                );
                mRemoveGlobalViewWatcherFn.set(this, unsetGlobalViewWatchFn);
            }
        );
    }

    makePrintable() {
        mDisplayMode.set(this, displayModes.PrintMode);

        mDocumentSvc.get(this).attr("title",
            `TrackerBase: Daily Tracking Summary for ${this.reportBounds.printFormatStart} to ${this.reportBounds.printFormatEnd}`
        );
    }

    returnToEditing() {
        mDisplayMode.set(this, displayModes.EditMode);

        mDocumentSvc.get(this).attr("title",
            "TrackerBase: Daily Tracking Summary"
        );
    }

    togglePrintOptions() {
        this._showPrintOptions = !this._showPrintOptions;
    }

    get showPrintOptions() {
        return this._showPrintOptions;
    }

    get displayOptionsHeaderIconClass() {
        return this._showPrintOptions ? "glyphicon-chevron-down" : "glyphicon-chevron-right";
    }

    sendReport() {
        if (!this.sendReportEnabled) {
            mLogSvc.get(this).info("Send Report Feature currently disabled.");
            return Promise.resolve(null);
        }

        const userMessagesSvc = mUserMessagesSvc.get(this);
        const inProgressMsgId = mUiMessages.get(this)
                                             .publishMessage(
                                                 userMessagesSvc.getUserMessage({msgId: 0x2000}),
                                                 MessageQueue.timeoutOverrideValue,
                                                 defaultMessageType);

        const sendReportPromise = mTrackerBaseReportsDataSvc.get(this).sendTrackingSummaryReport({
            start: this.reportBounds.reportStart,
            end: this.reportBounds.reportEnd,
            timeZone: this.reportBounds.reportTimeZone
        })
        .then((success) => mTimeoutSvc.get(this)(() => {
            if (success) {
                mUiMessages.get(this).removeMessageById(inProgressMsgId);
                mUiMessages.get(this).publishMessage(
                    userMessagesSvc.getUserMessage({msgId: 0x2001}),
                    defaultMessageTimeout,
                    successMessageType
                );
            }
            else {
                throw new Error("Report sent successfully, but there was an error notifying the user.");
            }
        },0))
        .catch((err) => mTimeoutSvc.get(this)(() => {
            const baseErrorMessage = userMessagesSvc.getUserMessage({msgId: 0x2A00});

            mUiMessages.get(this).removeMessageById(inProgressMsgId);
            mUiMessages.get(this).publishMessage(
                `${baseErrorMessage}: ${err.message}`,
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );

            throw err;
        },0))
        .catch((err) => {
            //TODO: Handle errors that can be handled.
            mLogSvc.get(this).error("An error occurred when requesting that the tracking summary report be sent.", err);
            // Swallow error here after logging it.
        });

        return sendReportPromise;
    }

}

TrackingSummaryReportController.$inject = [
    "$scope",
    "$document",
    "$log",
    "$filter",
    "$timeout",
    "$state",
    "TrackerBaseReportsData",
    "CurrentTimeZone",
    "AllOfficeTypes",
    "CalendarGlobalViewService",
    "UserMessages"
];

TrackingSummaryReportController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiBootstrap,
    uiRouter,
    kendoDirectives,
    TrackerBaseReportsDataServiceModule,
    FormatTimeZoneFilterModule,
    CurrentTimeZoneServiceModule,
    AllOfficeTypesServiceModule,
    StatusLabelComponentModule,
    CalendarGlobalViewServiceModule,
    UserMessagesServiceModule
];

TrackingSummaryReportController.template = require("./TrackingSummaryReport.tpl.html");

TrackingSummaryReportController.controllerAs = "dailySummaryReport";

export default TrackingSummaryReportController;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-reports/TrackingSummaryReportController.js
// module id = 6D5s
// module chunks = 1